import { useCallback, useMemo, useState } from "react";
import { View, Image } from "react-native";
import {
  getMedicationByBrandName,
  getSupplementByName,
} from "@src/packages/content";
import type { ContentBlock } from "@src/packages/content";
import MedicationFactsheetSheet from "../../../components/MedicationFactsheetSheet";
import type { MedicationUserContext } from "../../../components/MedicationFactsheetSheet";
import SupplementDetailSheet from "../../../components/SupplementDetailSheet";
import type { MedForm, MedicationScheduleItem } from "../schedule.types";
import { Row, StatusBadge, Tag } from "@louise/moon-ui";
import { semanticColors } from "@louise/moon-ui/theme/tokens";
import {
  isTriggerMedicationName,
  DOSE_STATUS_COLORS,
  DOSE_STATUS_ICONS,
  extractMedicationShortName,
  type DoseStatus,
} from "@src/packages/care/medications";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useMedicationFormCorrection } from "@src/packages/care/useMedicationFormCorrection";
import { resolveCoachProtocols } from "@src/packages/injection-coach/availability";
import {
  useOpenedVialEvidence,
  useUpdateMedication,
  vialEvidenceMatters,
} from "@src/packages/care";
import { InjectionCoachEntryButton } from "@src/packages/injection-coach/components/InjectionCoachEntryButton";

const pillCapsuleImage =
  require("../../../../assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("../../../../assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("../../../../assets/images/injection-pen.png") as number;

export function MedFormIcon({
  form,
  size = 28,
}: {
  form: MedForm;
  size?: number;
}) {
  switch (form) {
    case "pen":
      return (
        <Image
          source={injectionPenImage}
          style={{ width: size, height: size }}
          resizeMode="contain"
        />
      );
    case "vial":
      return (
        <Image
          source={vialSyringeImage}
          style={{ width: size, height: size }}
          resizeMode="contain"
        />
      );
    case "pill":
    case "suppository":
      return (
        <Image
          source={pillCapsuleImage}
          style={{ width: size, height: size }}
          resizeMode="contain"
        />
      );
  }
}

type MedRowProps = {
  item: MedicationScheduleItem;
  done?: boolean;
  /** Subtitle override — e.g. "Daily · until Day 14" for recurring in Coming Up */
  subtitle?: string;
  /** Called when the user taps "Edit" inside the factsheet — opens the add/edit medication modal */
  onEditMedication?: (item: MedicationScheduleItem) => void;
  /** Status of the logged dose, shown as a filled status mark */
  doneStatus?: DoseStatus;
  /** Override dosage badges when one medication has multiple recorded doses */
  dosageBadges?: string[];
};

type ResolvedFactsheet =
  | { kind: "medication"; item: ContentBlock<"medication"> }
  | { kind: "supplement"; item: ContentBlock<"supplement"> }
  | { kind: "none" };

function resolveMedicationFactsheet(
  item: MedicationScheduleItem,
): ResolvedFactsheet {
  const name = item.brandName ?? item.name;
  const directMatch = getMedicationByBrandName(name);
  if (directMatch) return { kind: "medication", item: directMatch };

  const normalizedName = name.trim().toLowerCase();
  if (normalizedName === "progesterone") {
    const selectedForm = item.userContext?.selectedForm?.toLowerCase() ?? "";
    const route = item.userContext?.route?.toLowerCase() ?? "";
    if (route.includes("vaginal") || selectedForm.includes("suppository")) {
      const m = getMedicationByBrandName("Endometrin");
      if (m) return { kind: "medication", item: m };
    }
    if (route.includes("intramuscular") || selectedForm.includes("vial")) {
      const m = getMedicationByBrandName("Progesterone in Oil");
      if (m) return { kind: "medication", item: m };
    }
    const m = getMedicationByBrandName("Prometrium");
    if (m) return { kind: "medication", item: m };
  }

  // Fallback: user may have added a supplement as a "medication" — route to
  // the supplement detail sheet so they still get content instead of the
  // empty fallback.
  const supplementMatch = getSupplementByName(name);
  if (supplementMatch) {
    return { kind: "supplement", item: supplementMatch };
  }

  // No content match — still open the medication sheet in its minimal
  // "coming soon" mode so users can edit / stop / delete the medication.
  return { kind: "none" };
}

export default function MedRow({
  item,
  done = false,
  subtitle,
  onEditMedication,
  doneStatus,
  dosageBadges,
}: MedRowProps) {
  const [openSheet, setOpenSheet] = useState<ResolvedFactsheet | null>(null);
  const [userCtx, setUserCtx] = useState<MedicationUserContext | undefined>(
    undefined,
  );
  const factsheetForRow = useMemo(
    () => resolveMedicationFactsheet(item),
    [item],
  );

  const handlePress = useCallback(() => {
    setOpenSheet(factsheetForRow);
    setUserCtx({
      ...item.userContext,
      originalName: item.brandName ?? item.name,
    });
  }, [factsheetForRow, item]);

  const closeSheet = useCallback(() => {
    setOpenSheet(null);
    setUserCtx(undefined);
  }, []);

  // A medication is "stopped" when the patient has explicitly halted it via
  // the Stop action — which prepends "Stopped" (or "Stopped: <reason>") to
  // notes. A regimen whose endDate naturally arrives is just "completed,"
  // not stopped, and shouldn't strike through.
  const isStopped = (() => {
    const notes = item.userContext?.notes;
    if (!notes) return false;
    return /^stopped(\s|:|$)/i.test(notes.trimStart());
  })();

  const defaultSubtitle = subtitle
    ? subtitle
    : item.sinceDay
      ? `Since ${item.sinceDay}`
      : undefined;
  const displayedDosages =
    dosageBadges && dosageBadges.length > 0
      ? dosageBadges
      : item.dosage
        ? [item.dosage]
        : [];
  const updateMedication = useUpdateMedication();
  const medicationIdPrefix = item.id.slice(0, 36);
  const medicationIdForSheet =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      medicationIdPrefix,
    )
      ? medicationIdPrefix
      : undefined;
  const isTrigger = isTriggerMedicationName(item.name);
  const hasTitleRightContent =
    displayedDosages.length > 0 ||
    item.recentlyChanged ||
    isStopped ||
    isTrigger;

  const StatusIcon = doneStatus ? DOSE_STATUS_ICONS[doneStatus] : null;

  const statusIndicator =
    doneStatus && StatusIcon ? (
      <View
        accessibilityLabel={`Logged as ${doneStatus}`}
        style={{
          width: 34,
          alignItems: "flex-end",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <View
          style={{
            width: 24,
            height: 24,
            borderRadius: 12,
            backgroundColor: DOSE_STATUS_COLORS[doneStatus],
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <StatusIcon
            size={14}
            color={semanticColors.text.inverse}
            strokeWidth={2.5}
          />
        </View>
      </View>
    ) : null;

  // Straight to the steps, without opening the factsheet first. A patient at the
  // fridge with a needle in her other hand should not have to read a factsheet to
  // reach them. Renders itself only where an approved procedure exists, so there
  // is nothing to gate here.
  /**
   * `medication.form` as the API stores it — **not** `item.form`.
   *
   * `item.form` is a `MedForm`, which has four values and exists to pick an
   * illustration: `pre_filled_syringe`, `injection_solution` and
   * `lyophilized_powder` all collapse into `"vial"` on the way in
   * (`API_FORM_TO_UI`). Passing it here looked right and silently answered
   * nothing — `deviceFromPrescription` does not recognise an icon name, so the
   * coach went on asking a question her calendar had already answered. The raw
   * value rides along in `userContext`, which is where the factsheet reads it
   * from too.
   *
   * Hooks cannot be called conditionally, so this is built whether or not the
   * shortcut renders. It does nothing until the coach calls it.
   */
  const recordedForm = item.userContext?.selectedForm;
  const correctMedicationForm = useMedicationFormCorrection(
    item.id,
    recordedForm,
  );
  // Whether her vial is already mixed, for the one medication where that
  // changes the procedure. Read here, passed down: the coach never fetches.
  const hasEarlierDose = useOpenedVialEvidence(item.id, {
    // Only where the answer can change a walk. One medication qualifies today,
    // and without this the read ran once per medication row on screen.
    enabled: vialEvidenceMatters(
      resolveCoachProtocols(item.brandName ?? item.name)?.protocols ?? [],
    ),
  });

  const coachShortcut = done ? null : (
    <InjectionCoachEntryButton
      variant="icon"
      // Same rule as the factsheet resolution above: the brand she was
      // prescribed, not the generic the row may be labelled with.
      medicationName={item.brandName ?? item.name}
      routes={[item.route]}
      prescribedDose={displayedDosages[0]}
      // A dose she confirms against her box, written to the record this row
      // stands for, so the row — and every other surface — reads right next
      // time. Only where the row knows which record that is.
      onCorrectDose={
        medicationIdForSheet
          ? (dose) =>
              updateMedication.mutate({
                medicationId: medicationIdForSheet,
                dose: dose.value,
                doseUnit: dose.unit,
              })
          : undefined
      }
      // What she told us when she added it, so the coach does not ask again —
      // and which record to write a correction to, which this row did not say.
      apiForm={recordedForm}
      patientMedicationId={item.id}
      onFormCorrected={correctMedicationForm}
      hasEarlierDose={hasEarlierDose}
      // Nothing, and spelled out rather than hidden behind a condition.
      //
      // This row has no dose logger. `trackable` and `onToggle` exist on it and
      // **no call site supplies either** — the cycle protocol list, the
      // treatment hub and its two step cards all omit them — so the checkbox
      // never renders here either. A gate written as `trackable ? onToggle :
      // undefined` reads like a rule and is a constant, which is worse than
      // nothing: it was committed as a fix and fixed nothing.
      //
      // So a walk opened from here ends by closing, and she logs the dose
      // herself. That is a real gap against #2267, not a decision — tracked in
      // #3708, and closing it means giving these rows a logging action, which
      // is a product call rather than a prop.
      onCoachFinished={undefined}
    />
  );

  const trailingControls =
    coachShortcut || statusIndicator ? (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        {coachShortcut}
        {statusIndicator}
      </View>
    ) : null;

  return (
    <>
      <Row
        surface="plain"
        leadingMeta={item.time}
        leadingMetaWidth={50}
        leading={<MedFormIcon form={item.form} />}
        leadingSize={30}
        title={extractMedicationShortName(item.name) || item.name}
        titleRight={
          hasTitleRightContent ? (
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 6,
                flexShrink: 1,
              }}
            >
              {isTrigger && <StatusBadge label="Trigger" tone="coral" />}
              {displayedDosages.map((dosage) => (
                <Tag key={dosage} label={dosage} tone="neutral" />
              ))}
              {item.recentlyChanged && (
                <StatusBadge label="Changed" tone="coral" />
              )}
              {isStopped && <StatusBadge label="Stopped" tone="coral" />}
            </View>
          ) : undefined
        }
        subtitle={defaultSubtitle || undefined}
        subtitleVariant="meta"
        onPress={handlePress}
        // Row captures for itself; the visible title is the medication.
        action="medication_row"
        muted={done}
        trailing={trailingControls}
        chevron={!statusIndicator}
        paddingHorizontal={16}
        paddingVertical={12}
        titleStyle={{
          color: isStopped
            ? semanticColors.text.tertiary
            : semanticColors.typography.primary,
          textDecorationLine: isStopped ? "line-through" : "none",
        }}
        subtitleStyle={{
          color: isStopped
            ? semanticColors.text.faint
            : semanticColors.typography.supporting,
          textDecorationLine: isStopped ? "line-through" : "none",
        }}
      />

      {openSheet?.kind === "supplement" ? (
        <SupplementDetailSheet
          item={openSheet.item}
          onClose={closeSheet}
          userContext={userCtx}
        />
      ) : (
        <MedicationFactsheetSheet
          item={openSheet?.kind === "medication" ? openSheet.item : null}
          onClose={closeSheet}
          userContext={userCtx}
          medicationId={medicationIdForSheet}
          fallbackName={userCtx ? (item.brandName ?? item.name) : undefined}
          onEditPress={
            onEditMedication
              ? () => {
                  closeSheet();
                  onEditMedication(item);
                }
              : undefined
          }
        />
      )}
    </>
  );
}
